import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkle, CheckCircle, Lightning, Star, ShieldCheck, Truck } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const HomePage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section - Full Screen Premium */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-[#020508]">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(59,130,246,0.08)_0%,_transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(212,175,55,0.05)_0%,_transparent_50%)]" />
        </div>

        <div className="container mx-auto px-8 md:px-16 lg:px-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.8 }}>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-[#D4AF37]/30 bg-[#D4AF37]/5 mb-8" data-testid="hero-badge">
                <Star size={14} weight="fill" className="text-[#D4AF37]" />
                <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Premium Quality Since 2024</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-7xl tracking-tight font-medium leading-[0.95] text-foreground mb-8" data-testid="hero-heading">
                Your Brand,
                <br />
                <span className="bg-gradient-to-r from-[#D4AF37] to-[#F5E6A3] bg-clip-text text-transparent">Our Craft</span>
              </h1>
              <p className="text-base md:text-lg leading-relaxed text-slate-400 mb-10 max-w-lg" data-testid="hero-description">
                Premium custom-labeled PET water bottles for hotels, weddings, corporate events & personal branding. Elevate every occasion.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/products">
                  <Button size="lg" className="rounded-none bg-[#D4AF37] hover:bg-[#C5A028] text-black font-medium px-8 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(212,175,55,0.3)]" data-testid="hero-cta-button">
                    Explore Collection <ArrowRight size={18} weight="bold" className="ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="rounded-none border-white/20 hover:bg-white/5 text-white px-8" data-testid="hero-secondary-button">
                    Get a Quote
                  </Button>
                </Link>
              </div>
              {/* Trust badges */}
              <div className="flex items-center gap-8 mt-12 pt-8 border-t border-white/10">
                <div className="text-center">
                  <p className="text-2xl font-heading font-semibold text-[#D4AF37]">500+</p>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500">Happy Clients</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-heading font-semibold text-[#D4AF37]">50K+</p>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500">Bottles Delivered</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-heading font-semibold text-[#D4AF37]">4.9</p>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500">Customer Rating</p>
                </div>
              </div>
            </motion.div>

            {/* Right: Product showcase */}
            <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.3 }}
              className="hidden lg:block relative">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-tr from-[#D4AF37]/10 to-transparent blur-2xl" />
                <img
                  src="https://customer-assets.emergentagent.com/job_premium-bottle-shop-1/artifacts/eize3blh_water.jpeg"
                  alt="TrueDroplet Custom PET Bottle"
                  className="relative w-full max-w-md mx-auto object-contain"
                  data-testid="hero-product-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Strip */}
      <section className="bg-[#0A0F1C] border-y border-white/5 py-6">
        <div className="container mx-auto px-8 flex flex-wrap justify-center gap-8 md:gap-16">
          <div className="flex items-center gap-2 text-slate-400"><ShieldCheck size={20} className="text-[#D4AF37]" /><span className="text-xs uppercase tracking-wider">FSSAI Certified</span></div>
          <div className="flex items-center gap-2 text-slate-400"><Truck size={20} className="text-[#D4AF37]" /><span className="text-xs uppercase tracking-wider">Pan India Delivery</span></div>
          <div className="flex items-center gap-2 text-slate-400"><Star size={20} weight="fill" className="text-[#D4AF37]" /><span className="text-xs uppercase tracking-wider">Premium Quality</span></div>
          <div className="flex items-center gap-2 text-slate-400"><Lightning size={20} className="text-[#D4AF37]" /><span className="text-xs uppercase tracking-wider">Fast Turnaround</span></div>
        </div>
      </section>

      {/* What We Do - Premium Cards */}
      <section className="py-28 px-8 md:px-16 lg:px-24 bg-[#050A10]" data-testid="features-section">
        <div className="container mx-auto">
          <motion.div initial="hidden" whileInView="visible" variants={fadeUp} transition={{ duration: 0.6 }} viewport={{ once: true }}
            className="text-center mb-20">
            <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4 block">What We Offer</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-foreground">
              Crafted for <span className="text-[#D4AF37]">Excellence</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: <Sparkle size={28} />, title: "Hotels & Restaurants", desc: "Custom-branded PET bottles with your hotel/restaurant logo, name and contact details. Perfect for luxury hospitality." },
              { icon: <CheckCircle size={28} />, title: "Weddings & Events", desc: "Personalized bottles with couple photos, names, and wedding date. Make your celebration unforgettable." },
              { icon: <Lightning size={28} />, title: "Corporate Branding", desc: "Professional branded bottles for conferences, offices, and corporate events. Impress your clients." }
            ].map((item, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" variants={fadeUp} transition={{ duration: 0.5, delay: i * 0.15 }} viewport={{ once: true }}
                className="group bg-[#0A0F1C] border border-white/5 p-10 hover:border-[#D4AF37]/30 transition-all duration-500 hover:-translate-y-1" data-testid={`feature-card-${i + 1}`}>
                <div className="text-[#D4AF37] mb-5 group-hover:scale-110 transition-transform duration-300">{item.icon}</div>
                <h3 className="text-xl font-heading font-normal mb-4 text-foreground">{item.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Showcase with Real Photos */}
      <section className="py-28 px-8 md:px-16 lg:px-24 bg-[#020508]" data-testid="use-cases-section">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" variants={fadeUp} transition={{ duration: 0.6 }} viewport={{ once: true }}
              className="lg:col-span-5">
              <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4 block">Our Portfolio</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium mb-8 text-foreground">
                Real Work,<br /><span className="text-[#D4AF37]">Real Results</span>
              </h2>
              <p className="text-base text-slate-400 leading-relaxed mb-8">
                From luxury hotels to beautiful weddings, our custom-labeled PET bottles have graced thousands of events across India.
              </p>
              <div className="space-y-5">
                {["Hotels & Restaurant branding", "Wedding & Event personalization", "Corporate gifting & conferences"].map((item, i) => (
                  <div key={i} className="flex items-center gap-3" data-testid={`use-case-${i + 1}`}>
                    <CheckCircle size={22} weight="fill" className="text-[#D4AF37] flex-shrink-0" />
                    <span className="text-sm text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/products" className="inline-block mt-10">
                <Button className="rounded-none bg-[#D4AF37] hover:bg-[#C5A028] text-black font-medium px-8">
                  View All Products <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </motion.div>

            <div className="lg:col-span-7 grid grid-cols-2 gap-4">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} viewport={{ once: true }}
                className="col-span-2 overflow-hidden border border-white/5" data-testid="showcase-image-1">
                <img src="https://customer-assets.emergentagent.com/job_premium-bottle-shop-1/artifacts/ih00g8bh_custom.jpeg"
                  alt="Wedding custom PET bottles" className="w-full h-72 object-cover hover:scale-105 transition-transform duration-700" />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} viewport={{ once: true }}
                className="overflow-hidden border border-white/5" data-testid="showcase-image-2">
                <img src="https://customer-assets.emergentagent.com/job_premium-bottle-shop-1/artifacts/eize3blh_water.jpeg"
                  alt="Restaurant branded bottles" className="w-full h-52 object-cover hover:scale-105 transition-transform duration-700" />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} viewport={{ once: true }}
                className="overflow-hidden border border-white/5" data-testid="showcase-image-3">
                <img src="https://customer-assets.emergentagent.com/job_premium-bottle-shop-1/artifacts/ndd42d9x_WhatsApp%20Image%202026-04-16%20at%201.57.48%20PM.jpeg"
                  alt="Corporate branded bottles" className="w-full h-52 object-cover hover:scale-105 transition-transform duration-700" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 px-8 md:px-16 lg:px-24 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-[#0A0F1C]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.06)_0%,_transparent_60%)]" />
        <div className="container mx-auto text-center relative z-10">
          <motion.div initial="hidden" whileInView="visible" variants={fadeUp} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-6 block">Start Today</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium mb-6 text-foreground">
              Ready to <span className="text-[#D4AF37]">Elevate</span> Your Brand?
            </h2>
            <p className="text-base text-slate-400 mb-10 max-w-xl mx-auto">
              Create custom-labeled PET water bottles that leave a lasting impression. Minimum order starts from just 100 bottles.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/products">
                <Button size="lg" className="rounded-none bg-[#D4AF37] hover:bg-[#C5A028] text-black font-medium px-10" data-testid="cta-button">
                  Start Customizing <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <a href="https://wa.me/918549880305?text=Hi%20TrueDroplet!%20I%20want%20to%20order%20custom%20water%20bottles" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="rounded-none border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-10">
                  WhatsApp Us
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
